import type { LucideIcon } from 'lucide-react';
import { Filter, FilterX, X } from 'lucide-react';
import { Badge } from '@reflex/ui/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@reflex/ui/components/ui/popover';
import { ASSOCIATION_CHIP_BADGE_CLASSNAME } from '@reflex/ui/components/shared/AssociationChip';
import { cn } from '@reflex/ui/lib/utils';

/** The taxonomies that render as a chip. Names the chip and its filter copy. */
export type TaxonomyNoun = 'Group' | 'Label';

export interface TaxonomyChipProps {
  /** Which taxonomy this is — drives the aria label, filter copy, and testids. */
  noun: TaxonomyNoun;
  /** The group's or label's display name. */
  name: string;
  icon: LucideIcon;
  /** Collapse to the icon when the strip runs out of room. */
  iconOnly?: boolean;
  /** The list is currently narrowed to this entry. */
  isActiveFilter: boolean;
  /** Narrow the list to this entry, or clear it. Absent outside a filterable list. */
  onToggleFilter?: () => void;
  /** Take this entry off the agent. Absent when the chip doesn't know whose it is. */
  onRemove?: () => void;
  /** Copy for the removal action — `Remove from group` vs `Remove label`. */
  removeActionLabel: string;
  /** The removal is in flight. */
  removePending?: boolean;
}

/**
 * The shell both taxonomy chips wear: a group membership and a label are the
 * same object on a row — a name you can narrow the list to, and take off.
 *
 * Unlike a pull-request or issue chip there is nothing external to open, so
 * this is not a link: clicking it opens the entry's actions. A group or label
 * is a lens on the list, so the primary action is "show me just these", with
 * "take this agent out of it" alongside.
 *
 * Both actions are conditional on context rather than shown disabled:
 * filtering needs an ambient list to filter, and removal needs to know which
 * agent the chip belongs to. Keeping the shell here is what stops the two
 * taxonomies from drifting into subtly different chips, the same way
 * {@link TaxonomyPickerDialog} keeps their pickers identical.
 */
export function TaxonomyChip({
  noun,
  name,
  icon: Icon,
  iconOnly,
  isActiveFilter,
  onToggleFilter,
  onRemove,
  removeActionLabel,
  removePending,
}: TaxonomyChipProps) {
  const lower = noun.toLowerCase();
  const testId = `agent-${lower}-chip`;

  return (
    <Popover>
      <PopoverTrigger
        aria-label={`${noun} ${name}`}
        data-testid={testId}
        className="inline-flex items-center rounded-md align-middle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={(e) => e.stopPropagation()}
      >
        <Badge
          variant="outline"
          className={cn(
            ASSOCIATION_CHIP_BADGE_CLASSNAME,
            // An active filter is the one state worth colouring: it tells you
            // at a glance which chip is doing the narrowing.
            isActiveFilter && 'border-ring/40 bg-ring/10 text-foreground',
          )}
        >
          <Icon className="h-2.5 w-2.5" aria-hidden="true" />
          {iconOnly ? null : <span className="max-w-24 truncate">{name}</span>}
        </Badge>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-1.5"
        data-testid={`${testId}-popover`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-2 py-1.5">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{name}</span>
        </div>
        <div className="flex flex-col">
          {onToggleFilter ? (
            <button
              type="button"
              data-testid={`${testId}-filter`}
              onClick={onToggleFilter}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
            >
              {isActiveFilter ? (
                <FilterX className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              ) : (
                <Filter className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              )}
              {isActiveFilter ? `Clear ${lower} filter` : `Filter to this ${lower}`}
            </button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              data-testid={`${testId}-remove`}
              disabled={removePending}
              onClick={onRemove}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {removeActionLabel}
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
